import React from 'react';
import classNames from 'classnames';
import type { SearchResultItem } from './GlobalSearchSlideout';
import styles from './GlobalSearchSlideout.module.scss';
import { SnippetText } from './SnippetText';
import { ReactComponent as DealsIcon } from '../../shared/icons/deals-icon.svg';
import { ReactComponent as DocumentIcon } from '../../shared/icons/document-icon.svg';
import { ReactComponent as TemplateIcon } from '../../shared/icons/icon-template.svg';
import { ReactComponent as ClausesIcon } from '../../shared/icons/icon-clauses.svg';
import { ReactComponent as UserIcon } from '../../shared/icons/user-icon.svg';
import { ReactComponent as ChecklistIcon } from '../../shared/icons/checklist-icon.svg';

const SparkleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor" {...props}>
    <path d="M8 0l1.6 4.6L14 6.2 9.6 7.8 8 12.4 6.4 7.8 2 6.2l4.4-1.6L8 0zM13 10l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2z" />
  </svg>
);

const TYPE_LABELS: Record<string, string> = {
  deal: 'Deal',
  document: 'Document',
  template: 'Template',
  clause: 'Clause',
  contact: 'Contact',
  checklist_item: 'Checklist',
};

const TYPE_ICONS: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  deal: DealsIcon,
  document: DocumentIcon,
  template: TemplateIcon,
  clause: ClausesIcon,
  contact: UserIcon,
  checklist_item: ChecklistIcon,
};

function getStatusPillClass(status?: string): string {
  if (!status) return '';
  const s = status.toLowerCase();

  if (['in progress', 'drafting', 'reviewing'].includes(s)) return styles.statusPill__blue;
  if (['completed', 'executed', 'complete', 'active'].includes(s)) return styles.statusPill__green;
  if (['lost', 'terminated'].includes(s)) return styles.statusPill__red;
  if (['archived', 'inactive'].includes(s)) return styles.statusPill__gray;
  if (['shared', 'signing', 'preparing to sign', 'incomplete'].includes(s)) return styles.statusPill__orange;

  return styles.statusPill__gray;
}

interface SearchResultsProps {
  results: SearchResultItem[];
  showWorkspace: boolean;
  onResultClick: (item: SearchResultItem) => void;
  onAskAi?: (item: SearchResultItem) => void;
}

/** Renders "Deal: Riverside" with the label emphasised, so the deal reads as a deal
 *  rather than as an undifferentiated line of small grey text. Falls back to plain
 *  text for subtitles that are not label-prefixed (a contact's email, for instance). */
const LABELLED_SUBTITLE = /^([A-Za-z][A-Za-z ]{1,11}):\s+(.+)$/;

const Subtitle: React.FC<{ text: string }> = ({ text }) => {
  const match = LABELLED_SUBTITLE.exec(text);
  if (!match) return <>{text}</>;

  return (
    <>
      <span className={styles.resultSubtitleLabel}>{match[1]}:</span> {match[2]}
    </>
  );
};

export const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  showWorkspace,
  onResultClick,
  onAskAi,
}) => {
  return (
    <>
      {results.map((item) => (
        <div
          key={`${item.resultType}-${item.id}`}
          className={styles.resultItem}
          onClick={() => onResultClick(item)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && onResultClick(item)}
        >
          {/* Type icon */}
          <div
            className={classNames(
              styles.resultTypeIcon,
              styles[`resultTypeIcon__${item.resultType}`]
            )}
          >
            {(() => {
              const IconComponent = TYPE_ICONS[item.resultType];
              return IconComponent ? <IconComponent /> : null;
            })()}
          </div>

          {/* Content */}
          <div className={styles.resultContent}>
            {/* Type and status are both metadata about the record, so they share a
                row and leave the title a full-width line of its own. */}
            <div className={styles.resultMetaRow}>
              <span className={classNames(styles.resultTypeBadge, styles[`resultTypeBadge__${item.resultType}`])}>
                {TYPE_LABELS[item.resultType] || item.resultType}
              </span>
              {item.status && (
                <span className={classNames(styles.statusPill, getStatusPillClass(item.status))}>
                  {item.status}
                </span>
              )}
              {onAskAi && item.resultType === 'document' && item.documentId && (
                <button
                  type="button"
                  className={styles.askAiButton}
                  title="Ask the assistant about this document"
                  onClick={(e) => {
                    // The whole card navigates; this must not.
                    e.stopPropagation();
                    onAskAi(item);
                  }}
                >
                  <SparkleIcon aria-hidden="true" />
                  Ask AI
                </button>
              )}
            </div>

            <div className={styles.resultTitle} data-testid="result-title">
              {item.title}
            </div>

            {item.subtitle && (
              <div className={styles.resultSubtitle}>
                <Subtitle text={item.subtitle} />
              </div>
            )}

            {/* A snippet is only present when the hit came from the body, so it
                doubles as the "matched in content" signal. */}
            {item.snippet && (
              <div className={styles.resultSnippet}>
                {item.clauseHeading && (
                  <span className={styles.resultClauseTag}>{item.clauseHeading}</span>
                )}
                <SnippetText snippet={item.snippet} />
              </div>
            )}

            {showWorkspace && item.workspaceName && (
              <div className={styles.resultWorkspace}>Workspace: {item.workspaceName}</div>
            )}
          </div>
        </div>
      ))}
    </>
  );
};
